import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import { X, Minus, Plus, Trash2, CreditCard } from "lucide-react";
import { useCart, inr } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#E8E4DF] focus:border-[#C5A059] px-0 py-3 text-[#2D1B36] placeholder-[#8A818F] transition-colors duration-300";

export const CartDrawer = () => {
  const { items, setQty, remove, clear, total, open, setOpen } = useCart();
  const { Razorpay } = useRazorpay();
  const navigate = useNavigate();
  const [step, setStep] = useState("cart");
  const [placing, setPlacing] = useState(false);
  const [payEnabled, setPayEnabled] = useState(true);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", address: "", note: "" });

  useEffect(() => {
    axios
      .get(`${API}/payments/config`)
      .then((r) => setPayEnabled(!!r.data?.enabled))
      .catch(() => setPayEnabled(false));
  }, []);

  const close = () => {
    setOpen(false);
    if (step !== "cart") setStep("cart");
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!payEnabled) {
      toast.error("Online payments are being set up. Please call 73565 72909 to order.");
      return;
    }
    if (!Razorpay) {
      toast.error("Payment is still loading — please try again in a moment.");
      return;
    }
    setPlacing(true);
    try {
      const res = await axios.post(
        `${API}/payments/order`,
        {
          customer_name: form.customer_name,
          phone: form.phone,
          address: form.address,
          note: form.note,
          items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        },
        { withCredentials: true }
      );
      const d = res.data;
      const rzp = new Razorpay({
        key: d.key_id,
        amount: d.amount,
        currency: d.currency,
        name: "Innus Lace Centre",
        description: `Order #${d.order_id}`,
        order_id: d.razorpay_order_id,
        prefill: { name: form.customer_name, contact: form.phone, email: form.email || undefined },
        theme: { color: "#4A2B58" },
        modal: {
          ondismiss: () =>
            toast.error("Payment not completed — your order is saved as payment pending."),
        },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              order_id: d.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            const placed = {
              id: d.order_id,
              name: form.customer_name,
              total: d.amount / 100,
              payment: "Paid online (Razorpay)",
              items: items.map((i) => ({ name: i.name, qty: i.qty, price: i.price })),
            };
            clear();
            setStep("cart");
            setOpen(false);
            navigate(`/order-success/${d.order_id}`, { state: { order: placed } });
          } catch {
            toast.error("Payment could not be verified. Please call the shop.");
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment. Please call us instead.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-[#2D1B36]/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[101] w-full max-w-md bg-[#FBF9F6] border-l border-[#E8E4DF] flex flex-col"
          >
            <div className="flex items-center justify-between p-8 pb-6 border-b border-[#E8E4DF]">
              <h2 className="font-serif-d text-2xl text-[#2D1B36]">
                {step === "cart" && "Your Bag"}
                {step === "checkout" && "Checkout"}
              </h2>
              <button
                onClick={close}
                data-testid="cart-close-button"
                className="text-[#8A818F] hover:text-[#2D1B36] transition-colors"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto p-8 space-y-8">
                  {items.length === 0 ? (
                    <p
                      data-testid="cart-empty-message"
                      className="font-serif-d italic text-xl text-[#8A818F] text-center pt-16"
                    >
                      Your bag is empty — the shelves are waiting.
                    </p>
                  ) : (
                    items.map((i) => (
                      <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-5">
                        <img
                          src={i.image}
                          alt={i.name}
                          className="w-20 h-24 object-cover bg-[#F2EFEB]"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between gap-3">
                            <h3 className="font-serif-d text-lg leading-snug text-[#2D1B36]">{i.name}</h3>
                            <button
                              onClick={() => remove(i.product_id)}
                              data-testid={`cart-remove-${i.product_id}`}
                              className="text-[#8A818F] hover:text-[#2D1B36] transition-colors shrink-0"
                            >
                              <Trash2 size={15} strokeWidth={1.5} />
                            </button>
                          </div>
                          <p className="text-sm text-[#514357] mt-1">
                            {inr(i.price)} <span className="text-[#8A818F] text-xs">{i.unit}</span>
                          </p>
                          <div className="mt-3 inline-flex items-center border border-[#E8E4DF]">
                            <button
                              onClick={() => setQty(i.product_id, i.qty - 1)}
                              data-testid={`cart-qty-minus-${i.product_id}`}
                              className="px-3 py-1.5 text-[#4A2B58] hover:bg-[#F2EFEB] transition-colors"
                            >
                              <Minus size={12} />
                            </button>
                            <span data-testid={`cart-qty-${i.product_id}`} className="px-4 font-accent text-sm">{i.qty}</span>
                            <button
                              onClick={() => setQty(i.product_id, i.qty + 1)}
                              data-testid={`cart-qty-plus-${i.product_id}`}
                              className="px-3 py-1.5 text-[#4A2B58] hover:bg-[#F2EFEB] transition-colors"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {items.length > 0 && (
                  <div className="p-8 pt-6 border-t border-[#E8E4DF] space-y-5">
                    <div className="flex justify-between items-baseline">
                      <span className="font-accent text-xs tracking-[0.25em] uppercase text-[#8A818F]">Total</span>
                      <span data-testid="cart-total" className="font-serif-d text-3xl text-[#2D1B36]">{inr(total)}</span>
                    </div>
                    <button
                      onClick={() => setStep("checkout")}
                      data-testid="proceed-to-checkout-button"
                      className="w-full bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#2D1B36] transition-colors duration-300"
                    >
                      Proceed to Payment
                    </button>
                  </div>
                )}
              </>
            )}

            {step === "checkout" && (
              <form onSubmit={placeOrder} className="flex-1 overflow-y-auto p-8 flex flex-col" data-testid="checkout-form">
                <div className="space-y-7 flex-1">
                  {[
                    { key: "customer_name", label: "Full Name", type: "text", ph: "Your name" },
                    { key: "phone", label: "Phone", type: "tel", ph: "10-digit mobile number" },
                    { key: "email", label: "Email (for payment receipt)", type: "email", ph: "you@example.com" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">{f.label}</label>
                      <input
                        required
                        type={f.type}
                        minLength={f.key === "phone" ? 10 : 2}
                        placeholder={f.ph}
                        value={form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        data-testid={`checkout-input-${f.key.replace("_", "-")}`}
                        className={inputCls}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">Delivery Address</label>
                    <textarea
                      required
                      minLength={5}
                      rows={3}
                      placeholder="House, street, landmark, town"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      data-testid="checkout-input-address"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div>
                    <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">Note (optional)</label>
                    <input
                      type="text"
                      placeholder="Colours, quantities, anything else"
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      data-testid="checkout-input-note"
                      className={inputCls}
                    />
                  </div>
                  <div className="flex items-center gap-3 bg-[#F2EFEB] px-4 py-3">
                    <CreditCard size={16} strokeWidth={1.5} className="text-[#2E6669] shrink-0" />
                    <p className="text-xs text-[#514357]">Pay securely online — UPI, cards & netbanking via Razorpay.</p>
                  </div>
                </div>
                <div className="pt-8 space-y-4">
                  <div className="flex justify-between items-baseline">
                    <span className="font-accent text-xs tracking-[0.25em] uppercase text-[#8A818F]">Total</span>
                    <span data-testid="checkout-total" className="font-serif-d text-3xl text-[#2D1B36]">{inr(total)}</span>
                  </div>
                  <button
                    type="submit"
                    disabled={placing || !payEnabled}
                    data-testid="place-order-button"
                    className="w-full bg-[#C5A059] text-[#2D1B36] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#DFCA9B] transition-colors duration-300 disabled:opacity-60"
                  >
                    {placing ? "Opening payment…" : payEnabled ? `Pay ${inr(total)} Online` : "Online Payment Coming Soon"}
                  </button>
                  {!payEnabled && (
                    <p data-testid="payments-disabled-note" className="text-xs text-[#8A818F] text-center">
                      Online payments are being set up — call 73565 72909 to place your order.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setStep("cart")}
                    data-testid="back-to-cart-button"
                    className="w-full font-accent text-[11px] tracking-[0.2em] uppercase text-[#8A818F] hover:text-[#2D1B36] transition-colors py-2"
                  >
                    Back to bag
                  </button>
                </div>
              </form>
            )}

          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
